import { useCallback, useEffect, useState } from "react";
import React from "react";
import { FileModelType } from "domains/file-manager/interfaces";
import ModelList, { ModelListProps } from "domains/models/components/ModelList";
import { getModelThumbnail } from "domains/models/utils";
import useRouter from "domains/navigation/hooks/useRouter";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Button from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from "domains/ui/components/Modal";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { useHandleApiError } from "infra/api/error";
import {
  GetModelsByModelIdApiResponse,
  useLazyGetModelsByModelIdQuery,
} from "infra/api/generated/api";

import { Box, Flex, Image, Text } from "@chakra-ui/react";

const MODAL_ID = "selectModelModal";

interface SidebarSelectModelProps {
  variant: ModelListProps["variant"];
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  model: GetModelsByModelIdApiResponse["model"] | undefined;
  setModel: (model: GetModelsByModelIdApiResponse["model"]) => void;
  isLoadingModel?: boolean;
}

export default React.memo(function SidebarSelectModel({
  variant,
  isOpen,
  setIsOpen,
  model,
  setModel,
  isLoadingModel,
}: SidebarSelectModelProps) {
  const router = useRouter();
  const { selectedProject } = useTeamContext();
  const handleApiError = useHandleApiError();
  const [isRouterReadyHandled, setIsRouterReadyHandled] =
    useState<boolean>(false);
  const [getModelTrigger] = useLazyGetModelsByModelIdQuery();
  const [isGetModelLoading, setIsGetModelLoading] = useState<boolean>(false);

  const isCanvas = variant === "canvas" || variant === "canvas-live";
  const isLoading = isGetModelLoading || isLoadingModel;

  useEffect(() => {
    if (!router.isReady || isRouterReadyHandled) return;
    const timeout = setTimeout(() => {
      setIsRouterReadyHandled(true);
      const modelId = router.query.modelId as string;
      if (!modelId && isCanvas) {
        setIsOpen(true);
      }
    }, 1);
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [
    isCanvas,
    isRouterReadyHandled,
    router.isReady,
    router.query.modelId,
    setIsOpen,
  ]);

  const handleListItemOpen = useCallback(
    async (file: FileModelType) => {
      setIsGetModelLoading(true);
      setIsOpen(false);
      try {
        const { model } = await getModelTrigger({
          modelId: file.id,
          projectId: selectedProject.id,
        }).unwrap();
        setModel(model);
        router.query.modelId = model.id;
        void router.push({
          pathname: router.pathname,
          query: router.query,
        });
        Track(AnalyticsEvents.Inference.SelectedModel, {
          id: model.id,
          name: model.name,
        });
      } catch (error) {
        handleApiError(error, "There was an error selecting the model");
      } finally {
        setIsGetModelLoading(false);
      }
    },
    [
      setIsOpen,
      getModelTrigger,
      selectedProject.id,
      setModel,
      router,
      handleApiError,
    ]
  );

  const handleModalClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return (
    <Box w="100%">
      <Button
        variant={isCanvas ? "secondary" : "ghost"}
        size="lg"
        onClick={() => setIsOpen(true)}
        px={isCanvas ? 2 : 6}
        h="64px"
        w="100%"
        colorScheme="white"
        borderRadius={isCanvas ? undefined : "none"}
        data-testid="inference-builder-model-selection-button"
        data-group
        isLoading={isLoading}
        tooltip={(model?.name ?? "").length > 35 ? model?.name : undefined}
      >
        <Flex align="center" justify="start" gap={2} w="100%">
          {model && (
            <Image
              w="40px"
              h="40px"
              borderRadius="base"
              objectFit="cover"
              alt="model thumbnail"
              aspectRatio="1"
              src={
                getModelThumbnail(model, {
                  width: 64,
                }).url
              }
            />
          )}

          <Text
            overflow="hidden"
            w="100%"
            pl={1}
            textColor={"textSecondary"}
            textAlign="start"
            _groupHover={{ textColor: "textPrimary" }}
            textOverflow="ellipsis"
            size="body.bold.md"
          >
            {model ? model.name : "Select Model"}
          </Text>

          <Icon
            color="textSecondary"
            px="5px"
            id="Ui/ChevronRight"
            _groupHover={{ color: "textPrimary" }}
          />
        </Flex>
      </Button>

      <Modal isOpen={isOpen} onClose={handleModalClose} variant="modern">
        <ModalOverlay />
        <ModalContent
          overflow="hidden"
          w="90vw"
          maxW="unset"
          h="80vh"
          p={0}
          bgColor="background.500"
        >
          <ModalCloseButton />
          <ModalBody w="100%" id={MODAL_ID}>
            <Flex direction="column" w="100%" h="80vh">
              <Flex
                align="center"
                justify="start"
                gap={8}
                w="100%"
                pl={5}
                py={4}
                borderBottomWidth={1}
              >
                <Text size="body.bold.lg">Choose a Model</Text>
              </Flex>

              <ModelList
                onOpen={handleListItemOpen}
                isInModal
                variant={variant}
                disableNotReady
              />
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
});
